import React, { useState, useEffect, lazy, Suspense } from "react";
import { createData, getData, getDataByID, setAuthToken, clearAuthToken } from "./api/API";
import { Routes, Route, useNavigate } from "react-router-dom";

import LoadingSpinner from "./components/modules/LoadingSpinner";
import ToastMessage from "./components/modules/ToastMessage";
import Navigation from "./components/modules/Navigation";
import TopHeader from "./components/layout/TopHeader";
import RightBox from "./components/modules/RigthsideBox";
import SignUpForm from "./components/modules/SignUpForm";
import Login from "./components/modules/Login";
import Logout from "./components/modules/Logout";
import { fetchCompanyDetails, company, getCompanyDetails } from "./utils/getcompany";

// Lazy-loaded Views for High Performance & Reduced Initial Bundle Size
const Home = lazy(() => import("./views/Home"));
const Company = lazy(() => import("./views/Company"));
const Products = lazy(() => import("./views/Products"));
const Employee = lazy(() => import("./views/Employee"));
const User = lazy(() => import("./views/User"));
const Role = lazy(() => import("./views/Role"));
const UpdateUserForm = lazy(() => import("./components/users/UpdateUser"));
const Invoice = lazy(() => import("./views/Invoice"));
const Retailers = lazy(() => import("./views/Retailers"));
const Items = lazy(() => import("./views/Items"));
const Expense = lazy(() => import("./views/Expense"));
const Reports = lazy(() => import("./views/Report"));
const Payment = lazy(() => import("./views/Payment"));
const ViewInvoice = lazy(() => import("./components/invoice/ViewInvoice"));
const GenerateInvoice = lazy(() => import("./components/invoice/GenerateInvoice"));
const Customers = lazy(() => import("./views/Customers"));
const DailySalesRecords = lazy(() => import("./views/DailySalesRecords"));
const EmployeesTable = lazy(() => import("./components/employee/GetAllEmployee"));
const MarketplaceOrders = lazy(() => import("./views/MarketplaceOrders"));
const ProductMappings = lazy(() => import("./views/ProductMappings"));
const SalesMatrixReport = lazy(() => import("./views/SalesMatrixReport"));


export function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

const App = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState({
    flag: false,
    message: "",
    bg: "",
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [rightboxOperation, setrightboxOperation] = useState();
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("userData");
      return saved ? JSON.parse(saved) : "";
    } catch (e) {
      return "";
    }
  });
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [featureAccessMap, setFeatureAccessMap] = useState({});
  const [companyLogo, setCompanyLogo] = useState("");

  useEffect(() => {
    setIsLoading(true);
    fetchCompanyDetails();
    (async () => {
      if (document.cookie.match("user_id")) {
        const userId = getCookie("user_id");
        const res = await createData("users/verifytoken", {
          user_id: userId,
        });
        if (res?.data?.status === "success") {
          setLoggedIn(true);
          setUserRole(res.data.user_role);

          try {
            const userRes = await getDataByID("users", userId);
            if (userRes?.data) {
              const fullUser = {
                ...res.data,
                ...userRes.data,
                user_name: userRes.data.name || userRes.data.user_name || res.data.user_name,
              };
              setUser(fullUser);
              localStorage.setItem("userData", JSON.stringify(fullUser));
            }
          } catch (err) {
            console.error("Error fetching user profile:", err);
          }

          const storedPermissions = localStorage.getItem("permissions");
          if (storedPermissions) {
            setUserPermissions(JSON.parse(storedPermissions));
          }
          const storedFeaturess = localStorage.getItem("features");
          if (storedFeaturess) {
            setFeatureAccessMap(JSON.parse(storedFeaturess));
          }
          setTimeout(() => {
            setIsLoading(false);
          }, 600);
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const details = await getCompanyDetails();
      if (details?.company_logourl) {
        setCompanyLogo(details.company_logourl);
      }
    })();
  }, []);

  const onLogin = async (data) => {
    const res = await createData("users/login", data);
    if (res?.data?.status === "success") {
      setAuthToken(res.data.token);
      document.cookie = `user_id=${res.data.user_id};expires=${new Date(
        new Date().setDate(new Date().getDate() + 7)
      ).toUTCString()}`;

      const usersData = await getDataByID("users", res.data.user_id);
      localStorage.setItem("userData", JSON.stringify(res.data));

      const currentUser = usersData?.data;
      if (!currentUser) {
        console.error("Logged-in user not found");
        return;
      }

      const matchedRole = currentUser?.role;
      if (!matchedRole) {
        console.error("Role not found for the user");
        return;
      }

      const fullUser = {
        ...res.data,
        ...currentUser,
        user_name: currentUser?.name || currentUser?.user_name || res.data?.user_name,
      };

      const features = matchedRole.permissions || [];
      localStorage.setItem("permissions", JSON.stringify(features));
      localStorage.setItem("userData", JSON.stringify(fullUser));
      await fetchFeatureAccessMap();
      setUserPermissions(features);
      setUserRole(res.data.user_role);
      setLoggedIn(true);
      setIsLoading(false);
      setUser(fullUser);
      navigate("/");
    } else {
      return res?.data;
    }
  };

  const onLogout = () => {
    clearAuthToken();
    document.cookie = `user_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    setLoggedIn(false);
    setUserRole("");
    setUserPermissions([]);
    setShowToast({
      flag: true,
      message: "Logged out Successfully",
      bg: "success",
    });
    navigate("/login");
  };

  const hasPermission = (featureName) => {
    if (!featureName || featureName === "Marketplace Orders" || featureName === "Product Mappings") {
      return true;
    }
    const featureId = featureAccessMap[featureName];
    if (!featureId) return true;
    return userPermissions.some(
      (perm) => perm?.feature_id === featureId
    );
  };

  const fetchFeatureAccessMap = async () => {
    try {
      const res = await getData("features");
      const map = {};
      res?.data?.forEach((feature) => {
        map[feature.feature_name] = feature._id;
      });
      localStorage.setItem("features", JSON.stringify(map));
      setFeatureAccessMap(map);
    } catch (error) {
      console.error("Error fetching features:", error);
    }
  };

  const routeConfig = [
    {
      path: "/company",
      element: <Company rightboxOperation={rightboxOperation} />,
      permission: { feature: "Organization" },
    },
    {
      path: "/dailysalesrecords",
      element: <DailySalesRecords rightboxOperation={rightboxOperation} />,
      permission: { feature: "Daily Sales Records" },
    },
    {
      path: "/employees",
      element: <Employee rightboxOperation={rightboxOperation} />,
      permission: { feature: "Organization" },
    },
    {
      path: "/getallemployees",
      element: <EmployeesTable />,
      permission: { feature: "Organization" },
    },
    {
      path: "/items",
      element: <Items rightboxOperation={rightboxOperation} />,
      permission: { feature: "Items" },
    },
    {
      path: "/reports",
      element: <Reports rightboxOperation={rightboxOperation} />,
      permission: { feature: "Reports" },
    },
    {
      path: "/sales-matrix",
      element: <SalesMatrixReport />,
      permission: { feature: "Reports" },
    },

    {
      path: "/products",
      element: <Products rightboxOperation={rightboxOperation} />,
      permission: { feature: "Organization" },
    },
    {
      path: "/users",
      element: <User rightboxOperation={rightboxOperation} />,
      permission: { feature: "Organization" },
    },
    {
      path: "/update-user/:userId",
      element: <UpdateUserForm />,
      permission: { feature: "Organization" },
    },
    {
      path: "/roles",
      element: <Role rightboxOperation={rightboxOperation} />,
      permission: { feature: "Organization" },
    },
    {
      path: "/retailers",
      element: <Retailers rightboxOperation={rightboxOperation} />,
      permission: { feature: "Retailers" },
    },
    {
      path: "/invoice",
      element: <Invoice rightboxOperation={rightboxOperation} />,
      permission: { feature: "Invoice" },
    },
    {
      path: "/generateinvoice/:retailer_id",
      element: <GenerateInvoice />,
      permission: { feature: "Invoice" },
    },
    {
      path: "/invoices/:invoiceId",
      element: <ViewInvoice />,
      permission: { feature: "Invoice" },
    },
    {
      path: "/payment",
      element: <Payment rightboxOperation={rightboxOperation} />,
      permission: { feature: "Payment" },
    },
    {
      path: "/payment/:id",
      element: <Payment />,
      permission: { feature: "Payment" },
    },
    {
      path: "/customers",
      element: <Customers rightboxOperation={rightboxOperation} />,
      permission: { feature: "Customers" },
    },
    {
      path: "/expenses",
      element: <Expense rightboxOperation={rightboxOperation} />,
      permission: { feature: "Expenses" },
    },
    {
      path: "/marketplaceorders",
      element: <MarketplaceOrders rightboxOperation={rightboxOperation} />,
      permission: { feature: "Marketplace Orders" },
    },
    {
      path: "/productmappings",
      element: <ProductMappings rightboxOperation={rightboxOperation} />,
      permission: { feature: "Product Mappings" },
    },
  ];

  return (
    <div className="erp-app-wrapper">
      {loggedIn ? (
        <>
          <TopHeader
            toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            userName={user?.user_name || user?.name || "Enterprise User"}
            userRole={userRole || user?.user_role || "Administrator"}
            onLogout={onLogout}
            companyLogo={companyLogo}
          />
          <div className="d-flex w-100 position-relative">
            <Navigation
              isOpen={isSidebarOpen}
              onLogout={onLogout}
              featureAccessMap={featureAccessMap}
              userPermissions={userPermissions}
              closeSidebar={() => setIsSidebarOpen(false)}
            />

            <main className="flex-grow-1 p-3 p-md-4 overflow-auto min-vh-100 bg-light">
              <div className="erp-content-container" style={{ maxWidth: "1440px", margin: "0 auto" }}>
                <RightBox employeeMethod={setrightboxOperation} />

                <div className="erp-main-content">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          isLoading ? (
                            <LoadingSpinner />
                          ) : (
                            <Home loggedIn={loggedIn} user={user} />
                          )
                        }
                      />
                      {routeConfig.map(
                        ({ path, element, permission }) =>
                          hasPermission(permission.feature) && (
                            <Route key={path} path={path} element={element} />
                          )
                      )}
                      <Route path="logout" element={<Logout />} />
                    </Routes>
                  </Suspense>
                </div>
              </div>
            </main>
          </div>
        </>
      ) : (
        <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Login onLogin={onLogin} />} />
              <Route path="signup" element={<SignUpForm />} />
              <Route path="login" element={<Login onLogin={onLogin} />} />
            </Routes>
          </Suspense>
        </div>
      )}
      <ToastMessage showToast={showToast} setShowToast={setShowToast} />
    </div>
  );
};

export default App;
